import {
    createContext,
    useState,
    useEffect,
    useCallback,
} from "react";
import { Col, Row, Container, Button } from "react-bootstrap";
import Loader2 from "../../../../../components/Loader2.jsx";
import { SnackbarAlert } from "../../../../../components/SnackbarAlert.js";
import api from "../../../../../service/api.js";
import List from "./list.js";
import Search from "./Search.js";
import download from "downloadjs";

export const ContextProvider = createContext(null);

export default function WriteOffApproval({
    title = "Write Off Approval",
    initialSearchValue = {},
    filterWriteOffs = null,
}) {
    const context = null;
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState([]);
    const [perPage, setPerPage] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [lastPage, setLastPage] = useState(1);
    const [createdByList, setCreatedByList] = useState([]);
    const [approvedByList, setApprovedByList] = useState([]);

    const [searchValue, setSearchValue] = useState({
        
        invoice_number: "",
        requestor_name: "",
        approver_name: "",
        status: "PENDING",
        approved_start_date: null,
        approved_end_date: null,
        reason: "write_off",
        ...initialSearchValue,
    });

    const [notif, setNotif] = useState({
        notifMsg: "",
        open: false,
        severity: "success",
    });

    const search = useCallback((page = 1) => {
        getWriteOffs(searchValue, page);
    }, [searchValue]);

    const getWriteOffs = useCallback(
        async (searchParams, page = 1) => {
            try {
                setLoading(true);
                const response = await api.get(
                        "tms/invoice/write-off",
                        { ...searchParams, page }
                    );

                const writeOffs = Array.isArray(response.data.write_offs)
                    ? response.data.write_offs
                    : [];
                const filteredWriteOffs = filterWriteOffs
                    ? writeOffs.filter(filterWriteOffs)
                    : writeOffs;

                setData(filteredWriteOffs);            // current page
                setPerPage(response.per_page);        // per page
                setCurrentPage(response.current_page); // current page
                setLastPage(response.last_page);      // total pages
                
                // Extract request_by and approved_by lists from response
                if (response.data.created_by) {
                    const createdByOptions = response.data.created_by;
                    setCreatedByList(createdByOptions);
                }
                
                if (response.data.approved_by) {
                    const approvedByOptions = response.data.approved_by;
                    setApprovedByList(approvedByOptions);
                }
            } catch (error) {
                console.error("Error fetching write-offs:", error);
                setNotif({
                    notifMsg: error.message || "An error occurred during fetching of data.",
                    open: true,
                    severity: "danger",
                });
            } finally {
                setLoading(false);
            }
        },
        []
    );

    
        const exportToExcel = async () => {
            try {
                // Await the API call
                const response = await api.getexcel("tms/invoice/write-off/export", searchValue);
    
                // Check content type
                const contentType = response.type || response.headers?.get?.("content-type");
    
                // If the server returned JSON instead of Excel, treat it as error
                if (contentType?.includes("application/json")) {
                    const text = await response.text();
                    let jsonData = {};
                    try {
                        jsonData = JSON.parse(text);
                    } catch {
                        setNotif({
                            notifMsg: "Unexpected response from server.",
                            open: true,
                            severity: "danger",
                        });
                        return;
                    }
    
                    if (jsonData.status === "ok" && jsonData.message) {
                        setNotif({
                            notifMsg: jsonData.message || "No data to export.",
                            open: true,
                            severity: "danger",
                        });
                        return;
                    }
    
                    if (jsonData.error) {
                        setNotif({
                            notifMsg: jsonData.message || "No data to export.",
                            open: true,
                            severity: "danger",
                        });                       
                        return;
                    }
    
                    // Fallback in case unknown JSON structure
                    setNotif({
                        notifMsg: "Failed to export. Server returned an error.",
                        open: true,
                        severity: "danger",
                    });
                    return;
                }
    
                // Only download if it’s a proper file
                const fileName = `${(title || "Write_Offs").replace(/\s+/g, "_")}_${new Date()
                    .toISOString()
                    .slice(0, 10)}.xlsx`;
    
                download(response, fileName, "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet");
    
                setNotif({
                    notifMsg: "Exported successfully!",
                    open: true,
                    severity: "success",
                });
            } catch (error) {
                console.error(error);
                setNotif({
                    notifMsg: "Export failed!",
                    open: true,
                    severity: "danger",
                });
            }
        };

    const actionLabel = title?.toLowerCase().includes("refund") ? "refund" : "write-off";

    const handleApprove = useCallback(async (writeOff) => {
        try {
            setLoading(true);
            await api.post(`tms/invoice/write-off/update-status/${writeOff.id}`, { status: "APPROVED" });
            setNotif({
                notifMsg: `${actionLabel.charAt(0).toUpperCase() + actionLabel.slice(1)} approved successfully`,
                open: true,
                severity: "success",
            });
            search();
        } catch (error) {
            setNotif({
                notifMsg: error.message || `Failed to approve ${actionLabel}`,
                open: true,
                severity: "danger",
            });
        } finally {
            setLoading(false);
        }
    }, [actionLabel, search]);

    const handleReject = useCallback(async (writeOff) => {
        try {
            setLoading(true);
            await api.post(`tms/invoice/write-off/update-status/${writeOff.id}`, { status: "REJECTED" });
            setNotif({
                notifMsg: `${actionLabel.charAt(0).toUpperCase() + actionLabel.slice(1)} rejected successfully`,
                open: true,
                severity: "success",
            });
            search();
        } catch (errors) {
            setNotif({
                notifMsg: errors.message || `Failed to reject ${actionLabel}`,
                open: true,
                severity: "danger",
            });
        } finally {
            setLoading(false);
        }
    }, [actionLabel, search]);


    const handleUpdated = useCallback(() => {
        search();
    }, [search]);

    const initialLoad = useCallback(() => {
        getWriteOffs(searchValue, 1);
    }, []);

    useEffect(() => {
        initialLoad();
    }, [initialLoad]);

    return (
        <ContextProvider.Provider value={context}>
            <Container fluid>
                <Row>
                    <Col md={12}>
                        <h3 className="fw-bolder m-0">{title}</h3>
                    </Col>
                </Row>

                <Row>
                    <Col xxl={12}>
                        <Search
                            setSearchValue={setSearchValue}
                            searchValue={searchValue}
                            search={search}
                            createdByList={createdByList}
                            approvedByList={approvedByList}
                        />
                    </Col>
                </Row>

                <Row className="mt-3 mb-4">
                    <Col md={12} className="d-flex justify-content-end gap-2">
                        <Button onClick={() => exportToExcel()}>Export Excel</Button>
                    </Col>
                </Row>

                <Row>
                    <Col xxl={12}>
                        {loading ? (
                            <Loader2 />
                        ) : (
                            <List
                                writeOffs={data ?? []}
                                currentPage={currentPage}
                                perPage={perPage}
                                lastPage={lastPage}
                                onPageChange={(page) => search(page)}
                                onApprove={handleApprove}
                                onReject={handleReject}
                                onUpdated={handleUpdated}
                            />
                        )}
                    </Col>
                </Row>

            </Container>
            <SnackbarAlert notif={notif} setNotif={setNotif} />
            
        </ContextProvider.Provider>
    );
}